import { Box } from "@mui/material";
import HomeTableSkeleton from "src/components/common/HomeTableSkeleton";
import { GridColDef } from "@mui/x-data-grid";
import { AxiosError, AxiosResponse } from "axios";
import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useStateOptions } from "src/hooks/useStateOptions";
import { ENTITY_OPTIONS_QUERY_KEY } from "src/hooks/useEntityOptions";
import { useNavigate } from "react-router-dom";
import DashboardWrapper from "src/components/common/DashboardWrapper";
import DataGrid from "src/components/common/DataGrid";
import EmptyView from "src/components/common/EmptyView";
import { Button } from "src/components/common/FormComponents";
import { MenuOption } from "src/components/common/MenuOption";
import { DeleteModal, SuccessModal } from "src/components/common/modal";
import { ROLES, userTabData } from "src/constants/common";
import {
  deleteUser,
  getAllUser,
  getUserDashboardDetails,
  userStatus,
} from "src/utils/api/dashboard";
import { dateAndTimeStamp, downloadSheet } from "src/utils/helper";
import { getOrganizationType } from "src/utils/api/roles";
import defaultStyles from "./styles";
import SearchBar from "src/components/common/SearchField";
import MultiSelect from "src/components/common/MultiSelect";
import { usePersistedFilters } from "src/hooks/usePersistedFilters";
import { useAppSelector } from "src/redux/store";

import { getStyles } from "src/styles/theme";

const SUMMARY_STAT_TYPES = ["BOTH_USERS", "ACTIVE_USERS", "INACTIVE_USERS"];

const mapUserRow = (user: any, sno: number) => ({
  userId: user.id,
  sno,
  name: user?.firstName,
  entityType: user.entityType,
  nameOfAuthorizedPerson: user?.nameOfAuthorizedPerson,
  designation: user.designation,
  email: user.email,
  phone: `${user.countryCode ?? "+91"} ${user.phoneNumber}`,
  userName: user.userName,
  status: user.status.status,
  state: user.address?.state || "",
  district: user.address?.district || "",
  createdAt: dateAndTimeStamp(user.createdAt, "IST"),
  role:
    user.role.name === "Implementing Agency"
      ? ROLES[user.userType] === "Individual"
        ? "Nodal Officer"
        : "Implementing Officer"
      : ROLES[user.userType],
});

const Users = () => {
  const [searchState, setSearchState] = useState("");
  const [tableRows, setTableRows] = useState([]);
  const userTabs = userTabData[1].value;

  const [userData, setUserData] = useState("");

  const { persisted, saveFilters } = usePersistedFilters("users");

  const role = useAppSelector((state: any) => state.root.user)?.data?.role;
  const isApprover = role === "Approver";

  const [queryParams, setQueryParams] = useState<{
    search?: string;
    role: string;
    entityTypeIds?: string;
    offset: number;
    limit: number;
  }>({
    search: persisted?.queryParams?.search ?? "",
    role: "Entity/ Green Credit Applicant",
    entityTypeIds: persisted?.queryParams?.entityTypeIds ?? "",
    offset: persisted?.queryParams?.offset ?? 0,
    limit: 50,
  });

  const [totalRecords, setTotalRecords] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  const [entityTypeFilter, setEntityTypeFilter] = useState<string[]>(
    persisted?.queryParams?.entityTypeIds
      ? persisted.queryParams.entityTypeIds.split(",")
      : [],
  );

  const styles = getStyles(defaultStyles);

  const [openModal, setOpenModal] = useState({
    open: false,
    heading: "",
    subheading: "",
    type: "",
    btnText2: "",
    btnText: "",
  });

  const [error, setError] = useState({
    open: false,
    heading: "",

    type: "",
  });

  const queryClient = useQueryClient();

  const { states: stateOptions } = useStateOptions();

  const { data: entityTypeResponse } = useQuery(
    ["getOrganizationType"],
    () => getOrganizationType(),
    {
      staleTime: 24 * 60 * 60 * 1000,
      cacheTime: 24 * 60 * 60 * 1000,
      refetchOnMount: false,
    },
  );
  const entityTypeOptions = entityTypeResponse?.data ?? [];

  useEffect(() => {
    saveFilters({ queryParams });
  }, [queryParams, saveFilters]);

  const { isLoading, refetch, isFetching } = useQuery(
    ["users", queryParams],
    () => getAllUser(queryParams),
    {
      onSuccess: (res: AxiosResponse) => {
        setTableRows(
          res.data.rows.map((user, idx) =>
            mapUserRow(user, queryParams.offset + idx + 1),
          ),
        );
        setTotalRecords(res.data.count ?? 0);
      },
      onError: (err: AxiosError) => {},
      refetchOnMount: true,
    },
  );

  const { data: dashboard } = useQuery(
    ["userDashboard"],
    () => getUserDashboardDetails(),
    {
      onSuccess: () => {},
      onError: () => {},
      refetchOnMount: true,
    },
  );

  const { mutate } = useMutation(
    "changeStatus",
    (data: { status: string; userId: string }) => userStatus(data),
    {
      onSuccess: () => {
        refetch();
        // status drives the entity dropdowns' `status=ACTIVE` filter
        queryClient.invalidateQueries(ENTITY_OPTIONS_QUERY_KEY);
      },
      onError: () => {},
    },
  );

  const deleteUserFun = useMutation(
    "deleteUSer",
    (data: string) => deleteUser(data),
    {
      onSuccess: (res: AxiosResponse) => {
        setError({
          open: true,
          heading: "User Successfully Deleted",
          type: "success",
        });
        refetch();
        queryClient.invalidateQueries(ENTITY_OPTIONS_QUERY_KEY);
      },
      onError: (err: AxiosError) => {
        setError({
          open: true,
          heading: err.response.data.message,
          type: "",
        });
        refetch();
      },
    },
  );

  const handleMenuOptions = (params, menuItem) => {
    mutate({
      status: menuItem.value,
      userId: params.row.userId,
    });
  };

  const handlePagination = (page: number) => {
    setQueryParams((prev) => ({
      ...prev,
      offset: prev.limit * page,
    }));
  };

  const handleSearch = useCallback((e: string) => {
    setQueryParams((prev) => ({
      ...prev,
      search: `${e}`,
      offset: 0,
    }));
  }, []);

  const updateEntityTypeFilter = (selected: string[]) => {
    setEntityTypeFilter(selected);
    setQueryParams((prev) => ({
      ...prev,
      entityTypeIds: selected.length ? selected.join(",") : "",
      offset: 0,
    }));
  };

  const toggleEntityTypeFilter = (id: string) => {
    const next = entityTypeFilter.includes(id)
      ? entityTypeFilter.filter((v) => v !== id)
      : [...entityTypeFilter, id];
    updateEntityTypeFilter(next);
  };

  const RenderButton = (params) => {
    return (
      <Button
        text={`${params?.row?.name}`}
        variant="text"
        onClick={() => {
          window.open(`/gca/view/${params?.row?.userId}`, "_blank");
        }}
        customStyles={{
          text: defaultStyles.renderButtonText,
        }}
      />
    );
  };

  const DeleteUser = (params) => {
    return (
      <Button
        text="Delete"
        variant="text"
        onClick={() => {
          setUserData(params.row.userId);
          setOpenModal({
            open: true,
            subheading: params.row.name,
            heading: "",
            btnText: "Yes",
            btnText2: "No",
            type: "success",
          });
        }}
        customStyles={{
          text: defaultStyles.deleteUser,
        }}
      />
    );
  };

  const handleModalClose = () => {
    setOpenModal({ ...openModal, open: false });
  };

  const handleConfirmationModalClose = () => {
    setError({ ...error, open: false });
  };

  const handleResponseModal = () => {
    setOpenModal({ ...openModal, open: false });
    deleteUserFun.mutate(userData);
  };

  const entityTableColumns: GridColDef[] = [
    { field: "id", headerName: "id", width: 75, sortable: false, hide: true },
    {
      field: "sno",
      headerName: "S No.",
      width: 75,
      sortable: false,
    },
    {
      field: "name",
      headerName: "Entity Name",
      width: 400,
      sortable: false,
      renderCell: RenderButton,
    },
    {
      field: "status",
      headerName: "Status",
      width: 175,
      sortable: false,
    },
    {
      field: "entityType",
      headerName: "Entity Type",
      width: 300,
      sortable: false,
    },
    {
      field: "nameOfAuthorizedPerson",
      headerName: "Name of authorized person",
      width: 250,
      sortable: false,
    },
    {
      field: "designation",
      headerName: "Designation",
      width: 200,
      sortable: false,
    },
    { field: "userName", headerName: "User Name", width: 175, sortable: false },
    {
      field: "email",
      headerName: "Email",
      width: 250,
      sortable: false,
    },
    {
      field: "phone",
      headerName: "Phone Number",
      width: 190,
      sortable: false,
    },
    {
      field: "state",
      headerName: "State",
      width: 150,
      sortable: false,
    },
    {
      field: "district",
      headerName: "District",
      width: 150,
      sortable: false,
    },
    {
      field: "createdAt",
      headerName: "Date of Registration",
      width: 200,
      sortable: false,
    },
    {
      field: "menuItem",
      headerName: "Active/Inactive",
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const status = (params?.row?.status || "").toString().toUpperCase();
        const color =
          status === "ACTIVE"
            ? "#2e7d32"
            : status === "INACTIVE"
            ? "#9e9e9e"
            : "inherit";
        return (
          <span style={{ color, fontWeight: 600 }}>{status || "—"}</span>
        );
      },
    },
    ...(isApprover
      ? [
          {
            field: "delete",
            headerName: "Delete User",
            width: 150,
            sortable: false,
            renderCell: DeleteUser,
          },
        ]
      : []),
  ];

  const exportToExcel2 = async () => {
    setIsExporting(true);
    try {
      const { offset, limit, ...filterParams } = queryParams;
      const res = await getAllUser(filterParams);
      const exportData = res.data.rows
        .map((user, idx) => mapUserRow(user, idx + 1))
        .map((row) => {
          // Map state ID to state name
          const stateName = stateOptions.find((s: any) => s.id === row.state)?.name || row.state || "";
          // Map district ID to district name
          const districtName = stateOptions
            .find((s: any) => s.id === row.state)
            ?.districts?.find((d: any) => d.id === row.district)?.name || row.district || "";
          return {
            ...row,
            state: stateName,
            district: districtName,
          };
        });
      downloadSheet(exportData);
    } finally {
      setIsExporting(false);
    }
  };

  const allStats = dashboard?.data?.stats ?? [];
  const summaryStats = allStats.filter((stat) =>
    SUMMARY_STAT_TYPES.includes(stat.type),
  );
  const entityTypeStats = allStats.filter(
    (stat) => !SUMMARY_STAT_TYPES.includes(stat.type),
  );

  return (
    <>
      <DashboardWrapper
        title="Entity/ GCA User Details"
        buttonIcon="/assets/svg/roundPlusIcon.svg"
        buttonText=""
        onButtonClick={() => {}}
        showBtn={!!tableRows.length}
      >
        <Box {...styles("statsCardWrapper")}>
          {summaryStats.map((stat, index) => (
            <Box
              key={index}
              sx={{
                flex: "1 1 220px",
                minWidth: "220px",
                height: "fit-content",
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                padding: "5px 14px",
                backgroundColor: "#1F8A7A",
                color: "#fff",
                cursor: "default",
              }}
            >
              <Box sx={{ fontSize: "12px", fontWeight: 500, letterSpacing: "0.6px", opacity: 0.9 }}>
                {stat?.description?.toUpperCase()}
              </Box>
              <Box sx={{ fontSize: "26px", fontWeight: 600 }}>{stat?.number}</Box>
            </Box>
          ))}
        </Box>
        <Box
          {...styles("entityTypeGridWrapper", {
            marginTop: "8px",
            gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
            gap: "8px",
          })}
        >
          {entityTypeStats.map((stat, index) => {
            const isSelected = entityTypeFilter.includes(stat.type);
            return (
              <Box
                key={index}
                onClick={() => toggleEntityTypeFilter(stat.type)}
                sx={{
                  borderRadius: "6px",
                  border: isSelected
                    ? "1px solid #1F8A7A"
                    : "1px solid #D0F1EC",
                  backgroundColor: isSelected ? "#1F8A7A" : "#F4FBFA",
                  boxShadow: isSelected
                    ? "0 2px 6px rgba(31, 138, 122, 0.35)"
                    : "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: "1px",
                  padding: "4px 10px",
                  cursor: "pointer",
                  transition: "0.2s",
                  "&:hover": {
                    backgroundColor: isSelected ? "#1c7c6d" : "#EBF9F7",
                    borderColor: isSelected ? "#1c7c6d" : "#36B1A0",
                  },
                }}
              >
                <Box
                  title={stat?.description}
                  sx={{
                    color: isSelected ? "#fff" : "custom.text.light",
                    fontSize: "10px",
                    fontWeight: 700,
                    letterSpacing: "0.3px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    opacity: isSelected ? 0.9 : 1,
                  }}
                >
                  {stat?.description?.toUpperCase()}
                </Box>
                <Box
                  sx={{
                    color: isSelected ? "#fff" : "custom.text.lighter",
                    fontSize: "16px",
                    fontWeight: 600,
                  }}
                >
                  {stat?.number}
                </Box>
              </Box>
            );
          })}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: "2%",
            padding: "10px 1%",
            background: "white",
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
          }}
        >
          <SearchBar
            getText={handleSearch}
            defaultValue={persisted?.queryParams?.search || ""}
            placeHolder="Search by entity name"
            customStyles={{ wrapper: defaultStyles.searchBarWrapper }}
          />
          <MultiSelect
            options={entityTypeOptions.map((entityType: any) => ({
              id: entityType.id,
              name: entityType.type,
              description: null,
            }))}
            label="Filter By Entity Type"
            value={entityTypeFilter}
            onChange={updateEntityTypeFilter}
          />
          <Button
            onClick={exportToExcel2}
            text={isExporting ? "Exporting..." : "Export"}
            disabled={isExporting}
          />
        </Box>
        <>
          <>
            {isLoading || isFetching ? (
              <HomeTableSkeleton showTabs={false} columns={7} />
            ) : !!totalRecords ? (
              <DataGrid
                rows={tableRows || []}
                columns={entityTableColumns}
                rowCount={Math.ceil(totalRecords / queryParams.limit)}
                rowsPerPageOptions={[50]}
                paginationMode="server"
                pageSize={1}
                pageNumber={queryParams.offset / queryParams.limit + 1}
                onChangePage={handlePagination}
                loading={isLoading || isFetching}
                columnBuffer={20}
                getRowId={(row) => `${row.id}-${row.userName}`}
              />
            ) : (
              <EmptyView
                heading="No user found"
                subheading=""
                onClick={() => {}}
              />
            )}
          </>
        </>
      </DashboardWrapper>
      <DeleteModal
        open={openModal.open}
        heading={"Are you sure you want to delete this user?"}
        subheading={openModal.subheading}
        btnText={openModal.btnText}
        btnText2={openModal.btnText2}
        type={openModal.type}
        onClick={() => handleResponseModal()}
        handleModalClose={() => handleModalClose()}
      ></DeleteModal>
      <SuccessModal
        open={error.open}
        heading={error.heading}
        type={error.type}
        onClick={() => handleResponseModal()}
        handleModalClose={() => handleConfirmationModalClose()}
      ></SuccessModal>
    </>
  );
};

export default Users;
